import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  dataCollectionAPI,
  RunTaskRequest,
  SyncRunStatusRequest,
  CleanupStaleStatusesRequest,
  SyncAllStatusesRequest
} from '@/services/api/dataCollection';
import { actorTasksAPI } from '@/services/api/actorTasks';

/**
 * Hook to fetch data collection tasks for a specific tenant
 * Uses prod.data_collection_actor_tasks as the source of truth
 * @param tenantId - Tenant ID to filter tasks (required)
 * @param includeDisabled - Include disabled tasks (default: true)
 */
export function useDataCollectionTasks(tenantId: string | null, includeDisabled: boolean = true) {
  return useQuery({
    queryKey: ['dataCollection', 'tasks', tenantId, includeDisabled],
    queryFn: () => {
      if (!tenantId) {
        throw new Error('Tenant ID is required to fetch tasks');
      }
      return actorTasksAPI.getTasksForTenant(tenantId, includeDisabled);
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2
  });
}

/**
 * Hook to trigger a data collection task
 */
export function useRunDataCollectionTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: RunTaskRequest) => dataCollectionAPI.runTask(request),
    onSuccess: () => {
      // Invalidate tasks query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['dataCollection', 'tasks'] });
      // Invalidate execution history if it exists
      queryClient.invalidateQueries({ queryKey: ['dataCollection', 'history'] });
    },
    retry: false
  });
}

/**
 * Hook to schedule a data collection task
 */
export function useScheduleDataCollectionTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskName,
      cronExpression,
      timeZone
    }: {
      taskName: string;
      cronExpression: string;
      timeZone?: string;
    }) => dataCollectionAPI.scheduleTask(taskName, cronExpression, timeZone),
    onSuccess: () => {
      // Invalidate tasks query to refresh schedules
      queryClient.invalidateQueries({ queryKey: ['dataCollection', 'tasks'] });
    },
    retry: false
  });
}

/**
 * Hook to get task execution status
 * Automatically polls if task is running
 */
export function useTaskStatus(runId: string | null) {
  return useQuery({
    queryKey: ['dataCollection', 'taskStatus', runId],
    queryFn: () => {
      if (!runId) throw new Error('Run ID is required');
      return dataCollectionAPI.getTaskStatus(runId);
    },
    enabled: !!runId,
    refetchInterval: query => {
      const data = query.state.data;
      // Poll every 5 seconds if task is running, otherwise don't poll
      if (data && 'status' in data && data.status === 'running') {
        return 5000;
      }
      return false;
    },
    retry: 2
  });
}

/**
 * Hook to fetch execution history
 * @param limit - Maximum number of executions to fetch
 * @param activeRunIds - Set of active run IDs to enable faster polling
 */
export interface ExecutionHistoryFilters {
  dateFrom?: string;
  status?: string;
  search?: string;
  schedule_id?: string;
}

export function useExecutionHistory(
  limit: number = 100,
  activeRunIds?: Set<string>,
  filters?: ExecutionHistoryFilters,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['dataCollection', 'executionHistory', limit, filters],
    queryFn: () => dataCollectionAPI.getExecutionHistory(limit, filters),
    enabled: options?.enabled !== false, // Support lazy loading
    staleTime: 5 * 60 * 1000, // 5 minutes - increased for faster loads with cached data
    refetchInterval: () => {
      // Reactively check if there are active runs (check current value, not initial value)
      const hasActiveRuns = activeRunIds && activeRunIds.size > 0;
      // If there are active runs, refetch more frequently (every 10 seconds)
      if (hasActiveRuns) {
        return 10 * 1000;
      }
      // Otherwise, refetch every 2 minutes to catch status updates
      return 2 * 60 * 1000;
    },
    refetchOnWindowFocus: false, // Disable to prevent blocking on tab switch
    refetchOnMount: false, // Use cached data first for instant loads
    retry: 2
  });
}

/**
 * Hook to fetch execution history for a specific schedule
 * Uses the schedule_id filter to get only executions triggered by this schedule
 * @param scheduleId - The schedule ID to filter by
 * @param limit - Maximum number of executions to fetch
 */
export function useScheduleExecutionHistory(
  scheduleId: string | null,
  limit: number = 100,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['dataCollection', 'scheduleExecutionHistory', scheduleId, limit],
    queryFn: () => {
      if (!scheduleId) {
        throw new Error('Schedule ID is required');
      }
      return dataCollectionAPI.getExecutionHistory(limit, { schedule_id: scheduleId });
    },
    enabled: !!scheduleId && options?.enabled !== false,
    staleTime: 2 * 60 * 1000, // 2 minutes for schedule-specific history
    refetchOnWindowFocus: false,
    retry: 2
  });
}

/**
 * Hook to get execution logs for a specific run
 */
export function useExecutionLogs(runId: string | null) {
  return useQuery({
    queryKey: ['dataCollection', 'executionLogs', runId],
    queryFn: () => {
      if (!runId) throw new Error('Run ID is required');
      return dataCollectionAPI.getExecutionLogs(runId);
    },
    enabled: !!runId,
    staleTime: 10 * 1000, // 10 seconds
    refetchInterval: 5 * 1000, // Poll every 5 seconds for logs
    retry: 2
  });
}

/**
 * Hook to sync a run's status from Apify
 */
export function useSyncRunStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ runId, request }: { runId: string; request?: SyncRunStatusRequest }) =>
      dataCollectionAPI.syncRunStatus(runId, request),
    onSuccess: () => {
      // Invalidate execution history and logs to refresh UI
      queryClient.invalidateQueries({ queryKey: ['dataCollection', 'history'] });
      queryClient.invalidateQueries({ queryKey: ['dataCollection', 'executionLogs'] });
    },
    retry: false
  });
}

/**
 * Hook to cleanup stale run statuses
 */
export function useCleanupStaleStatuses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request?: CleanupStaleStatusesRequest) =>
      dataCollectionAPI.cleanupStaleStatuses(request),
    onSuccess: () => {
      // Invalidate execution history to refresh UI
      queryClient.invalidateQueries({ queryKey: ['dataCollection', 'history'] });
    },
    retry: false
  });
}

/**
 * Hook to sync all stale statuses (Admin)
 */
export function useSyncAllStatuses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request?: SyncAllStatusesRequest) => dataCollectionAPI.syncAllStatuses(request),
    onSuccess: () => {
      // Invalidate execution history to refresh UI
      queryClient.invalidateQueries({ queryKey: ['dataCollection', 'history'] });
    },
    retry: false
  });
}
